import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { fbAddToCart } from "@/lib/pixel";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const KEY = "outlawd_cart";

const loadInitial = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (err) {
    console.warn("Could not parse cart from localStorage", err);
    return [];
  }
};

const itemMatches = (i, product_id, size, color) =>
  i.product_id === product_id && i.size === size && i.color === color;

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, { size = null, color = null } = {}) => {
    let alreadyInBag = false;
    setItems((prev) => {
      const idx = prev.findIndex((i) => itemMatches(i, product.product_id, size, color));
      if (idx >= 0) {
        alreadyInBag = true;
        return prev; // already in bag — no quantity stacking
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          name: product.name,
          price: product.price,
          image: product.images?.[0],
          quantity: 1,
          stock: product.stock ?? 1,
          size,
          color,
        },
      ];
    });
    if (alreadyInBag) toast.info("Already in your bag");
    else fbAddToCart(product);
    return !alreadyInBag;
  }, []);

  const remove = useCallback((product_id, size, color) => {
    setItems((prev) => prev.filter((i) => !itemMatches(i, product_id, size, color)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const total = useMemo(() => items.reduce((s, i) => s + i.price, 0), [items]);
  const count = useMemo(() => items.length, [items]);

  const value = useMemo(
    () => ({ items, add, remove, clear, total, count }),
    [items, add, remove, clear, total, count]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
